import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function CheckboxField({ f, value, onChange }) {
  if (!f.options?.length) {
    return (
      <label className="flex items-center gap-2 text-sm">
        <Checkbox checked={!!value} onCheckedChange={(v) => onChange(!!v)} data-testid={`enquiry-field-${f.key}`} /> {f.label}
      </label>
    );
  }
  const arr = Array.isArray(value) ? value : [];
  return (
    <div className="grid gap-2 sm:grid-cols-2" data-testid={`enquiry-field-${f.key}`}>
      {f.options.map((o) => (
        <label key={o} className="flex items-center gap-2 text-sm">
          <Checkbox checked={arr.includes(o)} onCheckedChange={(v) => onChange(v ? [...arr, o] : arr.filter((x) => x !== o))} /> {o}
        </label>
      ))}
    </div>
  );
}

function Field({ f, value, onChange }) {
  const tid = `enquiry-field-${f.key}`;
  if (f.type === "textarea") return <Textarea rows={3} value={value || ""} placeholder={f.placeholder} onChange={(e) => onChange(e.target.value)} data-testid={tid} />;
  if (f.type === "select") {
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={tid}><SelectValue placeholder={`Select ${f.label.toLowerCase()}`} /></SelectTrigger>
        <SelectContent className="z-[200]">
          {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  if (f.type === "radio") {
    return (
      <RadioGroup value={value || ""} onValueChange={onChange} className="flex flex-wrap gap-4" data-testid={tid}>
        {f.options.map((o) => (
          <label key={o} className="flex items-center gap-2 text-sm"><RadioGroupItem value={o} /> {o}</label>
        ))}
      </RadioGroup>
    );
  }
  if (f.type === "checkbox") return <CheckboxField f={f} value={value} onChange={onChange} />;
  return <Input type={f.type} value={value || ""} placeholder={f.placeholder} onChange={(e) => onChange(e.target.value)} data-testid={tid} />;
}

function Success({ message, onClose }) {
  return (
    <div className="py-6 text-center" data-testid="enquiry-success">
      <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-600" />
      <div className="mt-4 font-display text-xl font-bold">Enquiry submitted</div>
      <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">{message}</p>
      <Button className="mt-6" onClick={onClose} data-testid="enquiry-success-close">Close</Button>
    </div>
  );
}

export function EnquiryModal({ open, defaultCourse, onClose }) {
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");

  useEffect(() => {
    if (!open) return;
    setDone("");
    api.get("/enquiry-form").then((r) => {
      setForm(r.data);
      const courseField = r.data.fields.find((f) => f.key === "course_interest");
      setAnswers({
        full_name: user?.name || "", email: user?.email || "",
        ...(defaultCourse && courseField?.options.includes(defaultCourse) ? { course_interest: defaultCourse } : {}),
      });
    });
  }, [open, defaultCourse, user]);

  const missing = (form?.fields || []).filter((f) => f.required && [undefined, "", false].includes(answers[f.key])
    || (f.required && Array.isArray(answers[f.key]) && !answers[f.key].length));

  const submit = async (e) => {
    e.preventDefault();
    if (missing.length) return toast.error(`${missing[0].label} is required`);
    setBusy(true);
    try {
      const { data } = await api.post("/enquiries", { answers, source_page: window.location.pathname });
      setDone(data.message);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-2xl overflow-y-auto p-5 sm:p-7" data-testid="enquiry-modal">
        {done ? <Success message={done} onClose={onClose} /> : (
          <>
            <DialogHeader className="text-left">
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[hsl(var(--brand-orange))]">PilotYatra Admissions</div>
              <DialogTitle className="font-display text-2xl">{form?.title || "Enquire Now"}</DialogTitle>
              <DialogDescription>{form?.subtitle}</DialogDescription>
            </DialogHeader>
            {!form ? <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div> : (
              <form onSubmit={submit} className="mt-2 grid gap-4 sm:grid-cols-2" data-testid="enquiry-form">
                {form.fields.map((f) => (
                  <div key={f.key} className={["textarea", "radio", "checkbox"].includes(f.type) ? "sm:col-span-2" : ""}>
                    {!(f.type === "checkbox" && !f.options?.length) && (
                      <Label className="mb-1.5 block text-sm">{f.label}{f.required && <span className="text-rose-600"> *</span>}</Label>
                    )}
                    <Field f={f} value={answers[f.key]} onChange={(v) => setAnswers((a) => ({ ...a, [f.key]: v }))} />
                  </div>
                ))}
                <div className="flex flex-col-reverse gap-2 pt-2 sm:col-span-2 sm:flex-row sm:justify-end">
                  <Button type="button" variant="outline" onClick={onClose} data-testid="enquiry-close-btn"><X className="mr-1.5 h-4 w-4" /> Close</Button>
                  <Button type="submit" disabled={busy} className="bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90" data-testid="enquiry-submit-btn">
                    {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Submit Enquiry
                  </Button>
                </div>
              </form>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
